import { useEffect } from 'react';

import { usePlayerStore } from '../components/PlayerContextProvider';
import { PLAYER_DEBUG_KEY } from '../constants/debugKey';
import { playerLogger } from '../utils/playerLogger';

type VideoElementEventMap = HTMLVideoElementEventMap;

export type UsePlayerEventListenerParams<K extends keyof VideoElementEventMap> = {
  enabled?: boolean;
  handler: (event: VideoElementEventMap[K]) => void;
  location: string;
  type: K;
};

/**
 * Hook to add event listeners to the player's video element.
 * Handles cleanup automatically and provides proper TypeScript typings.
 *
 * @example
 * usePlayerEventListener({
 *   type: 'play',
 *   handler: () => console.log('Video started playing'),
 * });
 *
 * @example
 * usePlayerEventListener({
 *   type: 'timeupdate',
 *   handler: (event) => console.log('Time updated', event.target.currentTime),
 *   enabled: isFeatureEnabled,
 * });
 */
export const usePlayerEventListener = <K extends keyof VideoElementEventMap>({
  enabled = true,
  handler,
  location,
  type,
}: UsePlayerEventListenerParams<K>) => {
  const videoElement = usePlayerStore((state) => state.videoElement, {
    dangerouslyIncludeEventListeners: true,
  });

  useEffect(() => {
    if (!videoElement || !enabled) {
      return;
    }

    if (window.localStorage.getItem(PLAYER_DEBUG_KEY)) {
      playerLogger({ location, message: `Adding event listener for ${type}` });
    }

    videoElement.addEventListener(type, handler);

    return () => {
      if (window.localStorage.getItem(PLAYER_DEBUG_KEY)) {
        playerLogger({ location, message: `Removing event listener for ${type}` });
      }

      videoElement.removeEventListener(type, handler);
    };
  }, [videoElement, type, handler, enabled, location]);
};
